import { createApp, h, Fragment, nextTick, type App } from 'vue'
import VueTippy from 'vue-tippy'
import VueAxe, { VueAxePopup } from 'vue-axe'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { attachErrorHandler } from '@sentry/vue'

import 'tippy.js/dist/tippy.css'
import 'tippy.js/dist/border.css'
import 'tippy.js/themes/light.css'
import 'v-calendar/dist/style.css'
import 'core-js/modules/es.array.to-sorted'

import './utils/scrollbar'
import { initSentry } from './sentry'
import { loadLanguageAsync, getSessionLocale, i18nInst } from './i18n'
import config, { embeddedRuntime, hasZendeskProxy } from './config'
import { loadSession } from './composables/useSession'
import { illegallyEmbedded } from './modules/auth/utils/extension'
import { EXT_ID_KEY, TAB_ID_KEY } from './modules/extension/utils/messaging'

import ZuiApplication from './modules/application/ZuiApplication.vue'
import Application from './modules/application/Application.vue'
import DummyApplication from './modules/application/DummyApplication.vue'
import { popperOptions } from './components/Tippy'
import { sanitizeHook } from './utils/directives/sanitize.directive'
import klausEasterEgg from './utils/klausEasterEgg'
import { centrifuge } from './utils/push'
import { loadTicketsExist } from './composables/useTicketsExist'
import { loadNavigationComponent } from './zui-navigation'
import { loadZ2SunCoWidget } from './z2-sunco-widget'
import { loadZ2MessagingWidget } from './z2-messaging-widget'
import { initProductTracking } from './tracking'

initSentry()

const createDummyApp = async (locale: string) => {
  await loadLanguageAsync(locale)
  const app = createApp(DummyApplication)
  app.use(i18nInst)
  app.mount('#app')
  return app
}

// App setup
;(async () => {
  const params = new URLSearchParams(location.search)
  const extIdParam = params.get('extId')
  const tabIdParam = params.get('tabId')
  if (extIdParam) {
    sessionStorage.setItem(EXT_ID_KEY, extIdParam)
    localStorage.setItem(EXT_ID_KEY, extIdParam)
  } else {
    const stored = localStorage.getItem(EXT_ID_KEY)
    if (stored) sessionStorage.setItem(EXT_ID_KEY, stored)
  }
  if (tabIdParam) sessionStorage.setItem(TAB_ID_KEY, tabIdParam)

  const initialLocale = getSessionLocale()
  const dummyApp = await createDummyApp(initialLocale)

  if (illegallyEmbedded) {
    window.location.href = 'https://zendesk.com/qa'
    return
  }

  // Try to load auth session
  const session = await loadSession()

  if (session.fullPageRedirectFromError) return

  if (session.isAuthenticated && session.account) centrifuge.connect()

  const sessionLocale = getSessionLocale(session)

  if (sessionLocale !== initialLocale) {
    await loadLanguageAsync(sessionLocale)
  }

  // Check if the ZD account has sufficient tickets to show navigation pages
  if (hasZendeskProxy && session?.isAuthenticated && session.account) await loadTicketsExist()

  //  Load the bundled app-navigation web component
  await loadNavigationComponent()

  let app: App<Element>

  if (config.checkA11y) {
    app = createApp({
      setup() {
        return () =>
          h(Fragment, [h(session.features.zendeskUiNavigation ? ZuiApplication : Application), h(VueAxePopup)])
      },
    })
    app.use(VueAxe, {
      config: {
        rules: [
          // define rules: https://github.com/dequelabs/axe-core/blob/master/doc/rule-descriptions.md
        ],
        allowedOrigins: [],
      },
      runOptions: {
        runOnly: {
          type: 'tag',
          values: ['wcag2a'],
        },
      },
    })
  } else {
    app = createApp(session.features.zendeskUiNavigation ? ZuiApplication : Application)
  }

  if (session.features.newZ2MessagingWidget) {
    loadZ2MessagingWidget()
  } else {
    loadZ2SunCoWidget()
  }

  attachErrorHandler(app, { attachProps: true, logErrors: true } as any)

  app.use(i18nInst)

  app.use(VueQueryPlugin, {
    // navigator.onLine has some bugs on Macs and useQuery relies on it, so we set a different networkMode to fire off at least 1
    // request, even if the user's browser claims to be offline: https://github.com/TanStack/query/issues/5679#issuecomment-1623038531
    queryClientConfig: { defaultOptions: { queries: { refetchOnWindowFocus: false, networkMode: 'offlineFirst' } } },
  })

  app.use(VueTippy, {
    popperOptions,
    onShow: ({ props }) => !!props.content || !!props.title,
  })

  const { generateRouter } = await import('./router')
  const router = generateRouter()
  app.use(router)

  app.directive('sanitize', sanitizeHook)

  dummyApp.unmount()
  app.mount('#app')

  // Initialise Zendesk product usage tracking (pigateway). Gated by hasZendeskProxy.
  void initProductTracking()

  nextTick(() => {
    const loader = document.getElementById('app-loader')
    loader?.classList.add('hidden')
    setTimeout(() => loader?.remove(), 600)
  })

  console.info(klausEasterEgg)
  console.info(`🐾 🐾 ${embeddedRuntime() ? 'ZQA extension loaded' : 'Welcome to ZQA'} ${config.gitCommit}`)
})()
